import type { ActionTree } from 'vuex';
import type { IField } from '@lib-shared/fields/types/field';
import { translate } from '@lib-shared/helpers/translation.helper';
import { EFieldAction, EFieldMutation } from './field.constants';
import type { IFieldState } from './field.types';
import { FieldService } from '@signing-page/api/fields/services/field.service';
import type { IRootState } from '@signing-page/store/store';
import { alertNamespace, EAlertAction } from '@signing-page/alert/store/alert.constants';
import { PageHelper } from '@signing-page/helpers/page.helper';

export const actions: ActionTree<IFieldState, IRootState> = {
  async [EFieldAction.Load]({ dispatch, commit, rootState }) {
    commit(EFieldMutation.SetLoading, true);
    try {
      const { document } = rootState.document;
      let fields: IField[] = [];
      if (PageHelper.hasPDFPages(document)) {
        fields = await FieldService.loadFields();
      }
      dispatch(EFieldAction.LoadSuccess, fields);
    } catch (err) {
      dispatch(EFieldAction.LoadFailed);
    }
  },
  async [EFieldAction.LoadSuccess]({ commit }, fields: IField[]) {
    commit(EFieldMutation.SetFields, fields);
    commit(EFieldMutation.SetLoading, false);
  },
  async [EFieldAction.LoadFailed]({ commit, dispatch }) {
    commit(EFieldMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-document-fields'),
      {
        root: true,
      }
    );
  },
  async [EFieldAction.UpdatePageWidth]({ commit }, pageWidth: number) {
    commit(EFieldMutation.SetPageWidth, pageWidth);
  },
  async [EFieldAction.UpdateValue](
    { dispatch, commit, state },
    { fieldId, value }: { fieldId: string; value: string }
  ) {
    const oldField: IField = state.fields.find((field: IField) => field.id === fieldId);
    const updatedField: IField = { ...oldField, value };
    commit(EFieldMutation.UpdateField, updatedField);
    try {
      await FieldService.updateField(fieldId, value);
    } catch (error) {
      dispatch(EFieldAction.UpdateValueFailed, { error, field: oldField });
    }
  },
  async [EFieldAction.UpdateValueFailed](
    { commit, dispatch },
    { field }: { error: Error; field: IField }
  ) {
    commit(EFieldMutation.UpdateField, field);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('failed-to-update-fields'), {
      root: true,
    });
  },
  async [EFieldAction.AddVerifiedInitialId]({ commit, state }, fieldId: string) {
    if (state.verifiedInitialFieldIds.includes(fieldId)) {
      return;
    }
    const verifiedInitialFieldIds = [...state.verifiedInitialFieldIds, fieldId];
    commit(EFieldMutation.SetVerifiedInitialFieldIds, verifiedInitialFieldIds);
  },
};
